/**
 * Persistent client stores (ARCHITECTURE §8.5): IndexedDB `laserwatch` with a localStorage fallback.
 * The outbox driver is synchronous-at-call (the press record is written before any await by
 * keeping an in-memory mirror and persisting behind it); the kernel's interfaces stay sync.
 */
import { openDB, type IDBPDatabase } from 'idb';
import type { ClientSnapshot, MissionCacheStore, OutboxRecord, OutboxStore, TimeCache, TimeCacheStore } from '@lw/client-core';

interface Schema {
  outbox: { key: string; value: OutboxRecord };
  kv: { key: string; value: unknown };
}

let dbp: Promise<IDBPDatabase<Schema>> | null = null;
function db(): Promise<IDBPDatabase<Schema>> | null {
  if (typeof indexedDB === 'undefined') return null;
  if (!dbp) {
    dbp = openDB<Schema>('laserwatch', 1, {
      upgrade(d) {
        d.createObjectStore('outbox', { keyPath: 'cmdId' });
        d.createObjectStore('kv');
      },
    }).catch(() => {
      dbp = null;
      throw new Error('indexeddb unavailable');
    });
  }
  return dbp;
}

function lsGet<T>(k: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(k) ?? 'null') as T | null;
  } catch {
    return null;
  }
}
function lsSet(k: string, v: unknown): void {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    // quota / private mode: the NO OFFLINE PROTECTION warning covers this
  }
}

export interface Stores {
  outbox: OutboxStore;
  time: TimeCacheStore;
  mission: MissionCacheStore;
  cueLedger: { load(missionId: string): Record<string, unknown>; save(missionId: string, ledger: Record<string, unknown>): void };
  /** whether persistent storage is working at all */
  protected: () => boolean;
  ready: Promise<void>;
}

/** Load everything once at boot, then mirror in memory and persist asynchronously. */
export function createStores(scope: string): Stores {
  const K = (k: string): string => `lw.${scope}.${k}`;
  let outboxMem = new Map<string, OutboxRecord>();
  let timeMem: TimeCache | null = lsGet<TimeCache>(K('time'));
  let missionMem: ClientSnapshot | null = lsGet<ClientSnapshot>(K('mission'));
  let ledgerMem = new Map<string, Record<string, unknown>>();
  let ok = true;
  const d = db();
  const ready = (async () => {
    if (!d) {
      outboxMem = new Map((lsGet<OutboxRecord[]>(K('outbox')) ?? []).map((r) => [r.cmdId, r]));
      return;
    }
    try {
      const h = await d;
      const all = await h.getAll('outbox');
      outboxMem = new Map(all.filter((r) => r.cmdId.startsWith(`${scope}:`) || true).map((r) => [r.cmdId, r]));
      timeMem = ((await h.get('kv', K('time'))) as TimeCache | undefined) ?? timeMem;
      missionMem = ((await h.get('kv', K('mission'))) as ClientSnapshot | undefined) ?? missionMem;
      ledgerMem = new Map(Object.entries(((await h.get('kv', K('ledger'))) as Record<string, Record<string, unknown>> | undefined) ?? {}));
    } catch {
      ok = false;
      outboxMem = new Map((lsGet<OutboxRecord[]>(K('outbox')) ?? []).map((r) => [r.cmdId, r]));
    }
  })();
  const persistOutbox = (): void => {
    lsSet(K('outbox'), [...outboxMem.values()]);
    if (d) void d.then(async (h) => {
      const tx = h.transaction('outbox', 'readwrite');
      await tx.store.clear();
      for (const r of outboxMem.values()) await tx.store.put(r);
      await tx.done;
    }).catch(() => (ok = false));
  };
  const kvSet = (k: string, v: unknown): void => {
    lsSet(k, v);
    if (d) void d.then((h) => h.put('kv', v, k)).catch(() => (ok = false));
  };
  return {
    ready,
    protected: () => ok,
    outbox: {
      load: () => [...outboxMem.values()].map((r) => ({ ...r })),
      put: (r) => {
        outboxMem.set(r.cmdId, { ...r });
        persistOutbox();
      },
      remove: (id) => {
        outboxMem.delete(id);
        persistOutbox();
      },
    },
    time: {
      load: () => timeMem,
      save: (c) => {
        timeMem = c;
        kvSet(K('time'), c);
      },
    },
    mission: {
      load: () => missionMem,
      save: (s) => {
        missionMem = s;
        kvSet(K('mission'), s);
      },
    },
    cueLedger: {
      load: (missionId) => ledgerMem.get(missionId) ?? {},
      save: (missionId, ledger) => {
        ledgerMem.set(missionId, ledger);
        kvSet(K('ledger'), Object.fromEntries(ledgerMem));
      },
    },
  };
}
